import { UnreachableCaseError } from '@errors/unreachable';
import { PriceStrategy } from '@snap-types/models/enums';
import type { PriceStrategyConfigUpdate } from '@snap-types/models/price-strategy-config';
import { Decimal } from '@snap-types/types/decimal';

const priceStrategyTypesForSite: Record<string, PriceStrategy[]> = {
  adf: [
    PriceStrategy.Alpha,
    PriceStrategy.FixedCustomer,
    PriceStrategy.FixedDefault,
    PriceStrategy.FixedDiscount,
    PriceStrategy.FixedMarket,
    PriceStrategy.NewPartUserPrice,
    PriceStrategy.TargetContributionMargin,
    PriceStrategy.TargetGrossMargin,
    PriceStrategy.UserPrice,
    PriceStrategy.NewPart,
    PriceStrategy.UserImport,
  ],
  cap: [
    PriceStrategy.CAPPriceSegmentation,
    PriceStrategy.FixedCustomer,
    PriceStrategy.FixedDefault,
    PriceStrategy.FixedDiscount,
    PriceStrategy.FixedMarket,
    PriceStrategy.TargetContributionMargin,
    PriceStrategy.TargetGrossMargin,
    PriceStrategy.UserPrice,
  ],
  mmc: [
    PriceStrategy.PriceSegmentationWithTargetGrossMargin,
    PriceStrategy.FixedCustomer,
    PriceStrategy.FixedDefault,
    PriceStrategy.FixedDiscount,
    PriceStrategy.FixedMarket,
    PriceStrategy.NewPartUserPrice,
    PriceStrategy.PriceSegmentation,
    PriceStrategy.TargetContributionMargin,
    PriceStrategy.TargetGrossMargin,
    PriceStrategy.UserPrice,
  ],
  raf: [
    PriceStrategy.FixedCustomer,
    PriceStrategy.FixedDefault,
    PriceStrategy.FixedDiscount,
    PriceStrategy.PriceSegmentation,
    PriceStrategy.RAFFixedMarket,
    PriceStrategy.FixedMarket,
    PriceStrategy.RAFTargetGrossMargin,
    PriceStrategy.TargetContributionMargin,
    PriceStrategy.UserPrice,
  ],
  roc: [
    PriceStrategy.Alpha,
    PriceStrategy.FixedCustomer,
    PriceStrategy.FixedDefault,
    PriceStrategy.FixedDiscount,
    PriceStrategy.FixedMarket,
    PriceStrategy.NewPartUserPrice,
    PriceStrategy.PriceSegmentation,
    PriceStrategy.TargetContributionMargin,
    PriceStrategy.TargetGrossMargin,
    PriceStrategy.UserPrice,
    PriceStrategy.NewPart,
    PriceStrategy.UserImport,
  ],
  usa: [
    PriceStrategy.FixedCustomer,
    PriceStrategy.FixedDefault,
    PriceStrategy.FixedDiscount,
    PriceStrategy.FixedMarket,
    PriceStrategy.NewPartUserCost,
    PriceStrategy.NewPartUserPrice,
    PriceStrategy.PriceSegmentation,
    PriceStrategy.TargetContributionMargin,
    PriceStrategy.TargetGrossMargin,
    PriceStrategy.UserPrice,
    PriceStrategy.NewPart,
    PriceStrategy.UserImport,
  ],
  default: [
    PriceStrategy.FixedCustomer,
    PriceStrategy.FixedDefault,
    PriceStrategy.FixedDiscount,
    PriceStrategy.FixedMarket,
    PriceStrategy.NewPartUserPrice,
    PriceStrategy.PriceSegmentation,
    PriceStrategy.TargetContributionMargin,
    PriceStrategy.TargetGrossMargin,
    PriceStrategy.UserPrice,
  ],
};

export const priceStrategyConfigOptionLabels = {
  FixedCustomer: 'Fixed Customer',
  FixedDefault: 'Fixed Default',
  FixedDiscount: 'Fixed Discount',
  FixedMarket: 'Fixed Market',
  RAFFixedMarket: 'RAF Fixed Market',
  UserImport: 'User Import',
  UserPrice: 'User Price',
  NewPartUserPrice: 'New Part User Price',
  NewPartUserCost: 'New Part User Cost',
  TargetGrossMargin: 'Target Gross Margin',
  RAFTargetGrossMargin: 'Target Gross Margin',
  NewPart: 'New Part',
  TargetContributionMargin: 'Target Contribution Margin',
  PriceSegmentation: 'Price Segmentation (Price)',
  CAPPriceSegmentation: 'CAP Price Segmentation (Cost)',
  PriceSegmentationWithTargetGrossMargin:
    'Price Segmentation With Target Gross Margin (Cost)',
  Alpha: 'Auto Quote',
  ServiceCharge: 'Service Charge',
} as const satisfies Record<PriceStrategy, string>;

export function getAvailablePriceStrategies(
  siteCode: string,
  existingTypes: PriceStrategy[] = [],
): { all: PriceStrategy[]; available: PriceStrategy[] } {
  const siteAvailableTypes =
    priceStrategyTypesForSite[siteCode.toLowerCase()] ??
    priceStrategyTypesForSite.default;

  return {
    all: siteAvailableTypes,
    available: siteAvailableTypes.filter(
      (type) => !existingTypes.includes(type),
    ),
  };
}

export function getDefaultPriceStrategyParamsForType(
  type: PriceStrategy,
  {
    discountCodes,
    marketCodes,
  }: { discountCodes: string[]; marketCodes: string[] } = {
    discountCodes: [],
    marketCodes: [],
  },
): Extract<PriceStrategyConfigUpdate, { priceStrategyType: PriceStrategy }> {
  switch (type) {
    case PriceStrategy.TargetGrossMargin:
      return {
        sortOrder: 1,
        enabled: true,
        allowFallback: false,
        priceStrategyType: PriceStrategy.TargetGrossMargin,
        targetGrossMargin: new Decimal('0.3'),
      };
    case PriceStrategy.TargetContributionMargin:
      return {
        sortOrder: 1,
        enabled: true,
        allowFallback: false,
        priceStrategyType: PriceStrategy.TargetContributionMargin,
        targetContributionMargin: new Decimal('0.2'),
      };
    case PriceStrategy.PriceSegmentation:
      return {
        sortOrder: 1,
        enabled: true,
        allowFallback: false,
        priceStrategyType: PriceStrategy.PriceSegmentation,
        priceSegmentConfigs: [],
        quantityRange: new Decimal('10'),
      };
    case PriceStrategy.PriceSegmentationWithTargetGrossMargin:
      return {
        sortOrder: 1,
        enabled: true,
        allowFallback: false,
        priceStrategyType: PriceStrategy.PriceSegmentationWithTargetGrossMargin,
        priceSegmentConfigs: [],
        targetGrossMargin: new Decimal('0.3'),
      };
    case PriceStrategy.CAPPriceSegmentation:
      return {
        sortOrder: 1,
        enabled: true,
        allowFallback: false,
        priceStrategyType: PriceStrategy.CAPPriceSegmentation,
        priceSegmentConfigs: [],
        targetGrossMargin: new Decimal('30'),
      };
    case PriceStrategy.FixedDiscount:
      return {
        sortOrder: 1,
        enabled: true,
        allowFallback: false,
        priceStrategyType: PriceStrategy.FixedDiscount,
        discountCode: discountCodes[0] ?? '',
      };
    case PriceStrategy.FixedMarket:
      return {
        sortOrder: 1,
        enabled: true,
        allowFallback: false,
        priceStrategyType: PriceStrategy.FixedMarket,
        marketCode: marketCodes[0] ?? '',
      };
    case PriceStrategy.RAFFixedMarket:
      return {
        sortOrder: 1,
        enabled: true,
        allowFallback: false,
        priceStrategyType: PriceStrategy.RAFFixedMarket,
        marketCode: marketCodes[0] ?? '',
      };
    case PriceStrategy.RAFTargetGrossMargin:
      return {
        sortOrder: 1,
        enabled: true,
        allowFallback: false,
        priceStrategyType: PriceStrategy.RAFTargetGrossMargin,
        targetGrossMargin: new Decimal('0.3'),
      };
    case PriceStrategy.NewPart:
      return {
        sortOrder: 1,
        enabled: true,
        allowFallback: false,
        priceStrategyType: PriceStrategy.NewPart,
        targetGrossMargin: new Decimal('0.3'),
      };
    case PriceStrategy.FixedCustomer:
    case PriceStrategy.FixedDefault:
    case PriceStrategy.ServiceCharge:
    case PriceStrategy.UserImport:
    case PriceStrategy.UserPrice:
    case PriceStrategy.NewPartUserPrice:
    case PriceStrategy.NewPartUserCost:
    case PriceStrategy.Alpha:
      return {
        sortOrder: 1,
        enabled: true,
        allowFallback: false,
        priceStrategyType: type,
      } as PriceStrategyConfigUpdate;
    default:
      throw new UnreachableCaseError(type);
  }
}
